/**
 * Add / Edit a project laminate.
 *
 * Mirrors mobile's add-laminate + edit-laminate flows in a single
 * web slide-over (web slideovers can be re-purposed via the `item`
 * prop the same way `MaterialLibraryFormSlideOver` does).
 *
 * Fields (verbatim from mobile schema):
 *   • Room name      — required, autocomplete from `existingRooms`
 *   • Brand          — required, datalist hints from BRAND_HINTS
 *   • Finish         — required, chip picker (FINISH_OPTIONS) +
 *                      free-text override
 *   • Laminate code  — optional
 *   • Edge band code — optional
 *   • Swatch photo   — optional (uploaded to R2 with kind 'laminate'
 *                      keyed by the new doc id, OR by the laminate
 *                      id when editing)
 *   • Notes          — optional
 *
 * Save → `createLaminate` or `updateLaminate`. Delete → `deleteLaminate`
 * with inline confirm (no separate modal). Same slide-over chrome
 * (`.slide-over` / `.slide-overlay` / `.slide-header` / `.slide-body`)
 * used by every other slide-over on this app.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from '@/auth/AuthProvider';
import { RoomPickerDropdown } from '@/components/RoomPickerDropdown';
import {
  BRAND_HINTS,
  COMMON_ROOMS,
  FINISH_OPTIONS,
  createLaminate,
  deleteLaminate,
  updateLaminate,
  type Laminate,
} from '@/lib/laminates';
import { uploadFileToR2 } from '@/lib/r2Upload';

const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: '#fff',
  fontSize: 13,
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
};
const LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--text-tertiary)',
  marginBottom: 6,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
};

export type LaminateFormSlideOverProps = {
  open: boolean;
  onClose: () => void;
  orgId: string | null;
  projectId: string;
  /** Existing doc when editing, null when creating. */
  item: Laminate | null;
  /** Existing rooms in the project — drives the autocomplete /
   *  suggestion chips below the room name input. */
  existingRooms?: string[];
  /** Pre-fills the room name when creating (e.g. user clicked "+ Add
   *  in Kitchen"). Ignored when editing. */
  defaultRoomName?: string;
  onSaved?: (id: string) => void;
};

export function LaminateFormSlideOver({
  open,
  onClose,
  orgId,
  projectId,
  item,
  existingRooms = [],
  defaultRoomName,
  onSaved,
}: LaminateFormSlideOverProps) {
  const { user } = useAuth();
  const isEdit = Boolean(item);

  const [roomName, setRoomName] = useState('');
  const [brand, setBrand] = useState('');
  const [finish, setFinish] = useState('');
  const [laminateCode, setLaminateCode] = useState('');
  const [edgeBandCode, setEdgeBandCode] = useState('');
  const [notes, setNotes] = useState('');
  const [photoUrl, setPhotoUrl] = useState<string | undefined>(undefined);
  const [photoStoragePath, setPhotoStoragePath] = useState<string | undefined>(
    undefined,
  );
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const photoInputRef = useRef<HTMLInputElement | null>(null);

  // Reset / preload on every open.
  useEffect(() => {
    if (!open) return;
    if (item) {
      setRoomName(item.roomName);
      setBrand(item.brand);
      setFinish(item.finish);
      setLaminateCode(item.laminateCode ?? '');
      setEdgeBandCode(item.edgeBandCode ?? '');
      setNotes(item.notes ?? '');
      setPhotoUrl(item.photoUrl);
      setPhotoStoragePath(item.photoStoragePath);
    } else {
      setRoomName(defaultRoomName ?? '');
      setBrand('');
      setFinish('');
      setLaminateCode('');
      setEdgeBandCode('');
      setNotes('');
      setPhotoUrl(undefined);
      setPhotoStoragePath(undefined);
    }
    setConfirmDelete(false);
    setError(null);
    setUploadingPhoto(false);
  }, [open, item, defaultRoomName]);

  // Body scroll-lock while the slide-over is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Esc closes (unless busy).
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  // De-dupe + sort the project's existing rooms for the dropdown.
  // (The dropdown component handles its own filtering against the
  // COMMON_ROOMS list to avoid showing a room in both sections.)
  const projectRooms = useMemo(() => {
    const set = new Set<string>();
    for (const r of existingRooms) if (r) set.add(r);
    return Array.from(set).sort();
  }, [existingRooms]);

  async function handlePickPhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.currentTarget.files?.[0];
    e.currentTarget.value = ''; // allow re-picking the same file
    if (!file) return;
    if (!orgId) {
      setError('Sign-in lost — please refresh.');
      return;
    }
    setUploadingPhoto(true);
    setError(null);
    try {
      // We don't have a doc id yet when creating — use a temporary
      // ref id so the upload succeeds; the photoUrl ends up on the
      // laminate doc regardless. The R2 cap check uses orgId.
      const refId = item?.id ?? `new-${Date.now()}`;
      const { publicUrl, key } = await uploadFileToR2({
        file,
        kind: 'laminate',
        refId,
        orgId,
      });
      setPhotoUrl(publicUrl);
      setPhotoStoragePath(key);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn('[laminate] photo upload failed:', err);
      setError(`Photo upload failed: ${msg}`);
    } finally {
      setUploadingPhoto(false);
    }
  }

  function clearPhoto() {
    setPhotoUrl(undefined);
    setPhotoStoragePath(undefined);
  }

  async function handleSubmit() {
    setError(null);
    if (!user) {
      setError('Not signed in.');
      return;
    }
    if (!orgId) {
      setError('No organisation context.');
      return;
    }
    if (!roomName.trim()) {
      setError('Room name is required.');
      return;
    }
    if (!brand.trim()) {
      setError('Brand is required.');
      return;
    }
    if (!finish.trim()) {
      setError('Finish is required.');
      return;
    }
    setBusy(true);
    try {
      if (isEdit && item) {
        await updateLaminate(item.id, {
          roomName,
          brand,
          finish,
          laminateCode,
          edgeBandCode,
          notes,
          photoUrl,
          photoStoragePath,
        });
        onSaved?.(item.id);
      } else {
        const newId = await createLaminate({
          orgId,
          projectId,
          roomName,
          brand,
          finish,
          laminateCode: laminateCode || undefined,
          edgeBandCode: edgeBandCode || undefined,
          notes: notes || undefined,
          photoUrl,
          photoStoragePath,
          createdBy: user.uid,
        });
        onSaved?.(newId);
      }
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn('[laminate] save failed:', err);
      setError(msg || 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!item) return;
    setBusy(true);
    setError(null);
    try {
      await deleteLaminate(item.id);
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn('[laminate] delete failed:', err);
      setError(msg || 'Delete failed.');
    } finally {
      setBusy(false);
      setConfirmDelete(false);
    }
  }

  return (
    <>
      <div
        className={`slide-overlay${open ? ' open' : ''}`}
        onClick={() => !busy && onClose()}
      />
      <div className={`slide-over${open ? ' open' : ''}`}>
        <div className="slide-header">
          <h2 className="slide-title">
            <i className="ph ph-stack" />{' '}
            {isEdit ? 'Edit Laminate' : 'Add Laminate'}
          </h2>
          <button
            type="button"
            className="slide-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            <i className="ph ph-x" />
          </button>
        </div>

        <div className="slide-body">
          {error ? (
            <div
              style={{
                background: '#FEF2F2',
                color: 'var(--critical)',
                padding: 12,
                borderRadius: 6,
                fontSize: 13,
                border: '1px solid #FECACA',
              }}
            >
              {error}
            </div>
          ) : null}

          {/* Swatch photo */}
          <div>
            <label style={LABEL}>Swatch photo</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {photoUrl ? (
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: 8,
                    backgroundImage: `url(${photoUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    border: '1px solid var(--border)',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: 8,
                    background: '#F3F4F6',
                    color: 'var(--text-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 24,
                    flexShrink: 0,
                    border: '1px dashed var(--border)',
                  }}
                >
                  <i className="ph ph-image" />
                </div>
              )}
              <div style={{ flex: 1, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={uploadingPhoto || busy}
                >
                  {uploadingPhoto ? (
                    <>Uploading…</>
                  ) : photoUrl ? (
                    <>
                      <i className="ph ph-arrows-clockwise" /> Replace
                    </>
                  ) : (
                    <>
                      <i className="ph ph-upload-simple" /> Upload
                    </>
                  )}
                </button>
                {photoUrl ? (
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={clearPhoto}
                    disabled={uploadingPhoto || busy}
                  >
                    <i className="ph ph-x" /> Remove
                  </button>
                ) : null}
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePickPhoto}
                  style={{ display: 'none' }}
                />
              </div>
            </div>
          </div>

          {/* Room — proper picker dropdown (project rooms +
              common rooms + add-custom). */}
          <div>
            <label style={LABEL}>Room *</label>
            <RoomPickerDropdown
              value={roomName}
              onChange={setRoomName}
              existingRooms={projectRooms}
              commonRooms={COMMON_ROOMS}
              placeholder="Select or type a room"
              disabled={busy}
              autoFocus={!isEdit}
            />
          </div>

          {/* Brand + Finish — two columns on wider slide-over */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 12,
            }}
          >
            <div>
              <label style={LABEL}>Brand *</label>
              <input
                style={INPUT}
                value={brand}
                onChange={(e) => setBrand(e.currentTarget.value)}
                placeholder="Greenlam"
                list="lam-brand-hints"
                maxLength={40}
              />
              <datalist id="lam-brand-hints">
                {BRAND_HINTS.map((b) => (
                  <option key={b} value={b} />
                ))}
              </datalist>
            </div>
            <div>
              <label style={LABEL}>Finish *</label>
              <input
                style={INPUT}
                value={finish}
                onChange={(e) =>
                  setFinish(e.currentTarget.value.toUpperCase())
                }
                placeholder="MATTE"
                maxLength={30}
              />
            </div>
          </div>
          {/* Finish chip picker */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {FINISH_OPTIONS.map((f) => {
              const active = f === finish.toUpperCase();
              return (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFinish(f)}
                  style={{
                    padding: '4px 10px',
                    border: active
                      ? '1.5px solid var(--brand)'
                      : '1px solid var(--border)',
                    background: active ? 'var(--brand-bg, rgba(220,38,38,0.08))' : '#fff',
                    color: active ? 'var(--brand)' : 'var(--text-secondary)',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: active ? 700 : 500,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    letterSpacing: 0.4,
                  }}
                >
                  {f}
                </button>
              );
            })}
          </div>

          {/* Codes — two columns */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 12,
            }}
          >
            <div>
              <label style={LABEL}>Laminate code</label>
              <input
                style={{
                  ...INPUT,
                  fontFamily: 'Menlo, monospace',
                  letterSpacing: 0.3,
                }}
                value={laminateCode}
                onChange={(e) => setLaminateCode(e.currentTarget.value)}
                placeholder="GL-1102"
                maxLength={40}
              />
            </div>
            <div>
              <label style={LABEL}>Edge band code</label>
              <input
                style={{
                  ...INPUT,
                  fontFamily: 'Menlo, monospace',
                  letterSpacing: 0.3,
                }}
                value={edgeBandCode}
                onChange={(e) => setEdgeBandCode(e.currentTarget.value)}
                placeholder="EB-1102"
                maxLength={40}
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label style={LABEL}>Notes</label>
            <textarea
              style={{ ...INPUT, minHeight: 80, resize: 'vertical' }}
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
              placeholder="What is this laminate used for? (wardrobe doors, overhead cabinets, etc.)"
              maxLength={400}
            />
          </div>

          {/* Footer */}
          <div
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 8,
              paddingTop: 16,
              borderTop: '1px solid var(--border)',
            }}
          >
            {isEdit ? (
              confirmDelete ? (
                <div
                  style={{ display: 'flex', gap: 6, alignItems: 'center' }}
                >
                  <span style={{ fontSize: 12, color: 'var(--critical)' }}>
                    Delete this laminate?
                  </span>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setConfirmDelete(false)}
                    disabled={busy}
                  >
                    No
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleDelete}
                    disabled={busy}
                    style={{
                      background: 'var(--critical)',
                      borderColor: 'var(--critical)',
                    }}
                  >
                    Yes, delete
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setConfirmDelete(true)}
                  disabled={busy}
                  style={{ color: 'var(--critical)' }}
                >
                  <i className="ph ph-trash" /> Delete
                </button>
              )
            ) : (
              <div />
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSubmit}
                disabled={busy || uploadingPhoto}
              >
                {busy
                  ? 'Saving…'
                  : isEdit
                    ? 'Save changes'
                    : 'Add laminate'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
